import React, { useContext, useEffect, useState, forwardRef, useImperativeHandle, useRef } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import AppContext from "../../../../config/AppContext";

const CustomizedButtonDatePicker = forwardRef(({onClosePop=()=>{},...props}, ref) => {
    const { mode } = useContext(AppContext);
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [error, setError] = useState(props.error);
    const [selectedDate, setSelectedDate] = useState(
        props.value ? dayjs(props.value) : null
    );
    const initialDateRef = useRef(props.value ? dayjs(props.value) : null);
    const didSelectDateRef = useRef(false);

    const dayLabels = [
        t("RAL0765"),
        t("RAL0766"),
        t("RAL0767"),
        t("RAL0768"),
        t("RAL0769"),
        t("RAL0770"),
        t("RAL0771"),
    ];

    React.useEffect(() => {
        dayjs.locale(i18n.language || "en");
    }, [i18n.language]);

    const theme = createTheme({
        components: {
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        "&.MuiInputLabel-shrink": {
                            transform: "translate(14px, -6px) scale(0.75)",
                        },
                        "&.Mui-focused": {
                            display: "none",
                            border: "none",
                        },
                        "&.MuiInputLabel-root ": {
                            borderColor: "none",
                        },
                    },
                    "&:hover fieldset": {
                        display: "none",
                    },
                },
            },
            MuiDateCalendar: {
                styleOverrides: {
                    root: {
                        backgroundColor: mode === "light" ? "#ffffff" : "#1f1f1f",
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                    },
                },
            },
            MuiPickersPopper: {
                styleOverrides: {
                    root: {
                        marginTop: "10px !important",
                    },
                },
            },
            MuiPickersCalendarHeader: {
                styleOverrides: {
                    root: {
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                    },
                    label: {
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                    },
                    switchViewButton: {
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                    },
                },
            },
            MuiDayCalendar: {
                styleOverrides: {
                    root: {
                        backgroundColor: mode === "light" ? "#ffffff" : "#1f1f1f",
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                    },
                    weekDayLabel: {
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                    },
                    weekContainer: {
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                    },
                },
            },
            MuiPickersDay: {
                styleOverrides: {
                    root: {
                        color: mode === "light" ? "#242424" : "#f8f8f8",
                        "&.Mui-selected": {
                            color: "#fff",
                            borderRadius: "5px",
                            background:
                                "transparent linear-gradient(315deg, #FA9C2B 0%, #C1700E 100%) 0% 0% no-repeat padding-box !important",
                            borderColor: "#FA9C2B",
                        },
                        "&.MuiPickersDay-today": {
                            borderRadius: "5px",
                            border: "1px solid #fa9c2b !important",
                            color: mode === "light" ? "#242424" : "#f8f8f8",
                            background: mode === "dark" ? "#362C20" : "#FFF7ED",
                            "&:hover": {
                                color: "#fff",
                                borderRadius: "5px",
                                background:
                                    "transparent linear-gradient(315deg, #FA9C2B 0%, #C1700E 100%) 0% 0% no-repeat padding-box",
                                borderColor: "#FA9C2B",
                            },
                        },
                        ":hover": {
                            color: "#fff",
                            borderRadius: "5px",
                            background:
                                "transparent linear-gradient(315deg, #FA9C2B 0%, #C1700E 100%) 0% 0% no-repeat padding-box",
                            borderColor: "#FA9C2B",
                        },
                        "&.Mui-disabled": {
                            color:
                                mode === "light"
                                    ? "#24242480 !important"
                                    : "#f8f8f880 !important",
                        },
                    },
                },
            },
            MuiPickersArrowSwitcher: {
                styleOverrides: {
                    root: {
                        "& .MuiIconButton-root": {
                            color: mode === "light" ? "#242424" : "#f8f8f8",
                            "&.Mui-disabled": {
                                color: mode === "light" ? "#24242480 !important" : "#f8f8f880 !important",
                                opacity: 0.3,
                            },
                        },
                    },
                },
            },   

            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        color: mode === "light" ? "#242424" : "#f8f8f8", // Icon color
                    },
                },
            },
        },
    });

    const [anchorEl, setAnchorEl] = useState(null);

    useImperativeHandle(ref, () => ({
        openPicker: (buttonElement) => {
            initialDateRef.current = props.value ? dayjs(props.value) : null;
            setSelectedDate(initialDateRef.current);
            setAnchorEl(buttonElement);
            setOpen(true);
            didSelectDateRef.current = false;
            if (props.onOpenChange) props.onOpenChange(true); // Notify parent
        },
        closePicker: () => {
            setOpen(false);
            if (props.onOpenChange) props.onOpenChange(false); // Notify parent
        }
    }));

    useEffect(() => {
        setError(props.error);
    }, [props.error]);

    useEffect(() => {
        if (props.value && !dayjs(props.value).isSame(selectedDate, 'day')) {
            setSelectedDate(dayjs(props.value));
        }
    }, [props.value]);

    const handleDateChange = (newDate) => {
        didSelectDateRef.current = true;
        setSelectedDate(newDate);
        if (props.onChange) props.onChange(newDate);
        setOpen(false);
    };

    const handleClose = () => {
        if (!didSelectDateRef.current) {
            setSelectedDate(initialDateRef.current);
            if (props.onChange) props.onChange(initialDateRef.current);
        }
        didSelectDateRef.current = false;
        setOpen(false);
        onClosePop();
        if (props.onOpenChange) props.onOpenChange(false);
    };

    return (
        <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    open={open}
                    onClose={handleClose}
                    value={selectedDate}
                    onChange={handleDateChange}
                    minDate={dayjs('2025-01-01')}
                    slots={{
                        textField: () => null, // no input field
                    }}
                    slotProps={{
                        popper: {
                            anchorEl: anchorEl,
                            placement: "bottom-start",
                        },
                    }}
                />
            </LocalizationProvider>
        </ThemeProvider>
    );
});

export default CustomizedButtonDatePicker;
